// ═══════════════════════════════════════════════════════════════
// CostDashboard — Dashboard coûts & usage ITRIIA
// Graphique bar chart CSS, ventilation par projet/modèle
// Session 15 — Dashboard coûts
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { DollarSign, BarChart3, Cpu, FolderOpen, Zap, Calendar, TrendingUp, Layers, KeyRound } from 'lucide-react';

const formatCost = (usd) => {
  if (!usd || usd === 0) return '$0.00';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
};

const formatTokens = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
};

const CostDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [source, setSource] = useState('all'); // S43 — all | platform | user
  const [credits, setCredits] = useState(null);

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('oracle_user') || '{}'); } catch { return {}; }
  })();

  useEffect(() => {
    loadDashboard();
  }, [days, source]);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch(`/api/costs/dashboard?days=${days}&source=${source}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());

      // Solde OpenRouter (admin uniquement côté serveur, non-bloquant)
      try {
        const creditsRes = await fetch('/api/costs/credits', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (creditsRes.ok) setCredits(await creditsRes.json());
      } catch (e) {
        // Silencieux — non-admin reçoit 403, clé manquante reçoit 503
      }
    } catch (e) {
      console.error('Erreur dashboard coûts:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</div>;
  if (!data) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Aucune donnée disponible</div>;

  const maxDailyCost = Math.max(...data.daily.map(d => d.cost_usd), 0.001);
  const maxProjectCost = Math.max(...data.by_project.map(p => p.cost_usd), 0.001);
  const maxModelCost = Math.max(...data.by_model.map(m => m.cost_usd), 0.001);

  const cardStyle = {
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    borderRadius: '12px', padding: '20px',
  };

  const statBox = (icon, label, value, color) => (
    <div style={{
      ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px',
    }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '10px',
        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { size: 20, style: { color } })}
      </div>
      <div>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <DollarSign size={22} style={{ color: '#10b981' }} />
            Dashboard Coûts & Usage
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Suivi transparent des coûts LLM et services
          </p>
        </div>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* S43 — Filtre source */}
          <div style={{ display: 'flex', gap: '6px', padding: '4px', background: 'var(--input-bg)', borderRadius: '10px' }}>
            {[
              { key: 'all', label: 'Tous' },
              { key: 'platform', label: 'Plateforme' },
              { key: 'user', label: 'Clé perso' },
            ].map(s => (
              <button key={s.key} onClick={() => setSource(s.key)} style={{
                padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: '600',
                cursor: 'pointer', border: 'none', transition: 'all 0.2s',
                background: source === s.key ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent',
                color: source === s.key ? 'white' : 'var(--text-secondary)',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                {s.key === 'user' && <KeyRound size={10} />}
                {s.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setDays(d)} style={{
                padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
                cursor: 'pointer', border: 'none', transition: 'all 0.2s',
                background: days === d ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : 'var(--input-bg)',
                color: days === d ? 'white' : 'var(--text-secondary)',
              }}>
                {d}j
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* S43 — Ventilation par source (LLM uniquement, toujours globale) */}
      {data.by_source && (
        <div style={{ ...cardStyle, marginBottom: '20px', padding: '16px 20px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Layers size={12} /> Ventilation LLM par source ({days}j)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div style={{ padding: '10px 14px', background: 'rgba(59, 130, 246, 0.08)', borderRadius: 10, borderLeft: '3px solid #3b82f6' }}>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Plateforme</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{formatCost(data.by_source.platform.cost_usd)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{data.by_source.platform.events.toLocaleString()} appels</div>
            </div>
            <div style={{ padding: '10px 14px', background: 'rgba(16, 185, 129, 0.08)', borderRadius: 10, borderLeft: '3px solid #10b981' }}>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <KeyRound size={10} /> Clé perso
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{formatCost(data.by_source.user.cost_usd)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{data.by_source.user.events.toLocaleString()} appels</div>
            </div>
          </div>
        </div>
      )}

      {/* Solde OpenRouter */}
      {credits && (
        <div style={{
          ...cardStyle, marginBottom: '20px',
          borderLeft: `3px solid ${credits.percent_used < 50 ? '#22c55e' : credits.percent_used < 80 ? '#f59e0b' : '#ef4444'}`,
        }}>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Solde OpenRouter
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: credits.percent_used < 50 ? '#22c55e' : credits.percent_used < 80 ? '#f59e0b' : '#ef4444', marginBottom: '10px' }}>
            {'$'}{credits.remaining.toFixed(2)}
          </div>
          <div style={{ height: '6px', background: 'var(--input-bg)', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={{
              width: `${Math.min(credits.percent_used, 100)}%`,
              height: '100%', borderRadius: '3px',
              background: credits.percent_used < 50 ? '#22c55e' : credits.percent_used < 80 ? '#f59e0b' : '#ef4444',
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
            Consomme : {'$'}{credits.total_usage.toFixed(2)} / {'$'}{credits.total_credits.toFixed(2)} ({credits.percent_used}%)
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
            Rechargement via dashboard OpenRouter
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Mis a jour : {new Date(credits.fetched_at).toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {statBox(<DollarSign />, `Coût total (${days}j)`, formatCost(data.totals.cost_usd), '#10b981')}
        {statBox(<Zap />, 'Appels LLM', data.totals.events.toLocaleString(), '#3b82f6')}
        {statBox(<TrendingUp />, 'Tokens entrée', formatTokens(data.totals.input_tokens), '#8b5cf6')}
        {statBox(<Cpu />, 'Tokens sortie', formatTokens(data.totals.output_tokens), '#f59e0b')}
      </div>

      {/* Ventilation par service */}
      {data.by_service && data.by_service.length > 0 && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {data.by_service.map((s, i) => {
            const serviceLabels = {
              openrouter: { label: 'LLM (OpenRouter)', color: '#3b82f6', icon: '🤖' },
              whisper: { label: 'ITRIIA Voice', color: '#f59e0b', icon: '🎙️' },
              supadata: { label: 'Supadata', color: '#10b981', icon: '📺' },
              tts: { label: 'ITRIIA Studio', color: '#8b5cf6', icon: '🎧' },
              embedding: { label: 'Embeddings', color: '#06b6d4', icon: '🧬' },
              studio_dialecte: { label: 'Dialecte Studio', color: '#a78bfa', icon: '✨' },
            };
            const info = serviceLabels[s.service] || { label: s.service, color: '#6b7280', icon: '⚡' };
            return (
              <div key={i} style={{
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px',
                borderLeft: `3px solid ${info.color}`, flex: '1 1 180px', minWidth: '180px',
              }}>
                <span style={{ fontSize: '20px' }}>{info.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{info.label}</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: info.color }}>{formatCost(s.cost_usd)}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                    {s.events} appels
                    {s.service === 'tts' && s.total_chars > 0 && ` — ${formatTokens(s.total_chars)} chars`}
                    {s.service === 'whisper' && s.total_audio_seconds > 0 && ` — ${Math.round(s.total_audio_seconds / 60)}min audio`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Détail ITRIIA Studio par provider */}
      {data.tts_detail && data.tts_detail.length > 0 && (
        <div style={{
          ...cardStyle, marginBottom: '20px', borderLeft: '3px solid #8b5cf6',
        }}>
          <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🎧 Détail ITRIIA Studio
          </h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {data.tts_detail.map((d, i) => (
              <div key={i} style={{
                background: 'rgba(139,92,246,0.06)', borderRadius: '8px', padding: '10px 14px',
                border: '1px solid rgba(139,92,246,0.15)', flex: '1 1 200px',
              }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
                  {d.provider === 'openai' ? 'OpenAI' : 'ElevenLabs'} — {d.model}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#8b5cf6' }}>{formatCost(d.cost_usd)}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {d.events} génération{d.events > 1 ? 's' : ''} — {formatTokens(d.total_chars)} caractères
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* Graphique coûts par jour */}
        <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={16} style={{ color: '#3b82f6' }} />
            Coûts par jour
          </h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '140px' }}>
            {data.daily.map((d, i) => {
              const pct = (d.cost_usd / maxDailyCost) * 100;
              const date = new Date(d.day);
              const label = `${date.getDate()}/${date.getMonth() + 1}`;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <div title={`${label}: ${formatCost(d.cost_usd)} (${d.events} appels)`}
                    style={{
                      width: '100%', maxWidth: '28px', borderRadius: '4px 4px 0 0',
                      height: `${Math.max(pct, 2)}%`, minHeight: '3px',
                      background: 'linear-gradient(180deg, #3b82f6, #1d4ed8)',
                      transition: 'height 0.5s ease',
                      cursor: 'pointer',
                    }}
                  />
                  {data.daily.length <= 14 && (
                    <span style={{ fontSize: '9px', color: 'var(--text-secondary)', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {data.daily.length > 14 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '10px', color: 'var(--text-secondary)' }}>
              <span>{new Date(data.daily[0]?.day).toLocaleDateString('fr')}</span>
              <span>{new Date(data.daily[data.daily.length - 1]?.day).toLocaleDateString('fr')}</span>
            </div>
          )}
        </div>

        {/* Par projet */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FolderOpen size={16} style={{ color: '#10b981' }} />
            Par projet
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {data.by_project.map((p, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: 'var(--text)', fontWeight: '500' }}>{p.name}</span>
                  <span style={{ color: '#10b981', fontWeight: '600' }}>{formatCost(p.cost_usd)}</span>
                </div>
                <div style={{ height: '6px', background: 'var(--input-bg)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${(p.cost_usd / maxProjectCost) * 100}%`,
                    height: '100%', borderRadius: '3px',
                    background: 'linear-gradient(90deg, #10b981, #059669)',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {p.events} appels
                </div>
              </div>
            ))}
            {data.by_project.length === 0 && (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center', padding: '20px' }}>
                Aucune donnée
              </div>
            )}
          </div>
        </div>

        {/* Par modèle */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Cpu size={16} style={{ color: '#8b5cf6' }} />
            Par modèle
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {data.by_model.map((m, i) => {
              const shortName = m.model.split('/').pop();
              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: 'var(--text)', fontWeight: '500' }}>{shortName}</span>
                    <span style={{ color: '#8b5cf6', fontWeight: '600' }}>{formatCost(m.cost_usd)}</span>
                  </div>
                  <div style={{ height: '6px', background: 'var(--input-bg)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(m.cost_usd / maxModelCost) * 100}%`,
                      height: '100%', borderRadius: '3px',
                      background: 'linear-gradient(90deg, #8b5cf6, #7c3aed)',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {m.events} appels — {formatTokens(m.input_tokens)} in / {formatTokens(m.output_tokens)} out
                  </div>
                </div>
              );
            })}
            {data.by_model.length === 0 && (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center', padding: '20px' }}>
                Aucune donnée
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostDashboard;
